import { useSelectedCustomerIdParam } from "@/features/hub/selectedCustomerContext";
import { getSurveysOptions, ParticipantCountQueryOptions } from "@pulse/api/queryApi";
import { faArrowDownWideShort, faArrowUpWideShort } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {
    Alert,
    AlertDescription,
    Button,
    Card,
    formatSurveyDate,
    Input,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "frontend-components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Pagination from "./Pagination";
import StatusBadge from "./StatusBadge";
import SurveyLink from "./SurveyLink";
import SurveyTableCard from "./SurveyTableCard";
import type { SurveyTableRow } from "./types";

export default function SurveyTable({ pulseInstanceId }: { pulseInstanceId: number }) {
    const { t, i18n } = useTranslation();
    const customerId = useSelectedCustomerIdParam();

    const {
        data: surveys,
        isPending: isSurveysPending,
        isError: isSurveysError,
    } = useQuery(getSurveysOptions({ pulseInstanceId }));

    const participantCountsQuery = useQueries({
        queries: (surveys ?? []).map((survey) =>
            ParticipantCountQueryOptions({ surveyId: survey.id }),
        ),
        combine: (results) => {
            const data: SurveyTableRow[] = (surveys ?? []).map((survey, index) => ({
                ...survey,
                participantCount: results[index]?.data ?? { total: 0, answered: 0 },
            }));

            return {
                data,
                isPending: results.some((result) => result.isPending),
                isError: results.some((result) => result.isError),
            };
        },
    });

    const isLoading = isSurveysPending || participantCountsQuery.isPending;
    const isError = isSurveysError || participantCountsQuery.isError;

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<SurveyTableRow>();

        return [
            columnHelper.accessor("title", {
                header: () => t("common.title"),
                cell: (context) => (
                    <SurveyLink
                        survey={context.row.original}
                        customerId={customerId}
                        className="block min-w-50 max-w-120 text-wrap wrap-break-word"
                    />
                ),
            }),
            columnHelper.accessor("status", {
                header: () => t("surveyStatus.status"),
                cell: (status) => <StatusBadge status={status.getValue()} />,
            }),
            columnHelper.accessor("participantCount", {
                header: () => t("survey.responses"),
                cell: (info) => {
                    const { total, answered } = info.getValue();
                    return (
                        <span>
                            {answered} / {total}
                        </span>
                    );
                },
            }),
            columnHelper.accessor(
                (row) =>
                    row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : null,
                {
                    id: "fullName",
                    header: () => t("common.createdBy"),
                    cell: (info) => info.getValue() ?? t("common.unknown"),
                },
            ),
            columnHelper.accessor("createdAt", {
                header: () => t("common.created"),
                cell: (info) => formatSurveyDate(info.getValue(), i18n.language),
            }),
            columnHelper.accessor("reminderSentAt", {
                header: () => t("survey.reminderSentTitle"),
                cell: (info) => {
                    const reminderSentAt = info.getValue();
                    return reminderSentAt ? formatSurveyDate(reminderSentAt, i18n.language) : "";
                },
            }),
        ];
    }, [customerId, i18n.language, t]);

    const table = useReactTable<SurveyTableRow>({
        data: participantCountsQuery.data,
        columns,
        initialState: {
            pagination: {
                pageSize: 15,
                pageIndex: 0,
            },
            sorting: [{ id: "createdAt", desc: true }],
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const rows = table.getRowModel().rows;
    const createdAtColumn = table.getColumn("createdAt");
    const sortDirection = createdAtColumn?.getIsSorted();

    return (
        <Card className="gap-4 bg-transparent p-0 shadow-none xl:bg-compendia-card xl:p-8 xl:shadow-sm">
            <Input
                value={(table.getState().globalFilter as string | undefined) ?? ""}
                onChange={(event) => table.setGlobalFilter(event.target.value)}
                className="h-10 w-full max-w-90 text-xs"
                placeholder={t("common.search")}
                disabled={isError}
            />

            {isError ? (
                <Alert variant="error">
                    <AlertDescription>{t("survey.loadError")}</AlertDescription>
                </Alert>
            ) : (
                <>
                    <div className="flex flex-col gap-3 xl:hidden">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() =>
                                    createdAtColumn?.toggleSorting(sortDirection === "asc")
                                }
                                className="h-auto gap-1.5 border-0 p-0 text-label font-normal text-compendia-muted hover:text-chameleon-green"
                            >
                                {t("common.created")}
                                <FontAwesomeIcon
                                    icon={
                                        sortDirection === "asc"
                                            ? faArrowUpWideShort
                                            : faArrowDownWideShort
                                    }
                                />
                            </Button>
                            <span className="text-label text-compendia-muted">
                                {t("common.total")}: {table.getFilteredRowModel().rows.length}
                            </span>
                        </div>

                        {isLoading ? (
                            <div className="flex flex-col gap-3" aria-label={t("common.loading")}>
                                {Array.from({ length: 5 }, (_, index) => (
                                    <Skeleton key={index} className="h-38 rounded-card" />
                                ))}
                            </div>
                        ) : rows.length > 0 ? (
                            <ul className="flex flex-col gap-3">
                                {rows.map((row) => (
                                    <SurveyTableCard
                                        key={row.id}
                                        survey={row.original}
                                        customerId={customerId}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <Alert variant="info">
                                <AlertDescription>{t("common.no_results")}</AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <div className="border-compendia-border bg-compendia-card hidden overflow-hidden rounded-card border xl:block">
                        <Table className="text-xs">
                            <TableHeader className="bg-compendia-greige-10">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow
                                        key={headerGroup.id}
                                        className="border-compendia-border hover:bg-transparent"
                                    >
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                scope="col"
                                                aria-sort={
                                                    header.column.getIsSorted() === "asc"
                                                        ? "ascending"
                                                        : header.column.getIsSorted() === "desc"
                                                          ? "descending"
                                                          : "none"
                                                }
                                            >
                                                {header.column.getCanSort() ? (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        className="h-auto w-full justify-start gap-1 border-0 p-0 text-xs font-medium text-compendia-foreground hover:text-chameleon-green"
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column.columnDef.header,
                                                                  header.getContext(),
                                                              )}
                                                        {header.column.getIsSorted() === "desc" && (
                                                            <FontAwesomeIcon
                                                                icon={faArrowDownWideShort}
                                                            />
                                                        )}
                                                        {header.column.getIsSorted() === "asc" && (
                                                            <FontAwesomeIcon
                                                                icon={faArrowUpWideShort}
                                                            />
                                                        )}
                                                    </Button>
                                                ) : header.isPlaceholder ? null : (
                                                    flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext(),
                                                    )
                                                )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 5 }, (_, rowIndex) => (
                                        <TableRow
                                            key={rowIndex}
                                            className="border-compendia-border"
                                        >
                                            {columns.map((column, columnIndex) => (
                                                <TableCell key={`${column.id ?? columnIndex}`}>
                                                    <Skeleton className="h-5 w-full rounded-full" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : rows.length > 0 ? (
                                    rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            className="border-compendia-border hover:bg-compendia-greige-10"
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext(),
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={table.getAllColumns().length}
                                            className="py-8 text-center text-compendia-muted"
                                        >
                                            {t("common.no_results")}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {!isLoading && <Pagination table={table} />}
                </>
            )}
        </Card>
    );
}
